/**
 * Owns the three.js world: scene, camera, renderer. Nothing IFC-specific.
 *
 * Uses OBF.PostproductionRenderer (not SimpleRenderer) because it is the
 * built-in route to an architectural look — GTAO, edges, outlines, SMAA — and
 * OBC.ShadowedScene for grounded cascade shadows.
 *
 * API verified against @thatopen/components 3.4.8 type definitions.
 */
import * as THREE from "three";
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import { CAMERA, SCENE, SHOW_ENGINE_LOGO } from "../core/config";

/**
 * `OBC.World` is a non-generic interface; `SimpleWorld` is the generic class
 * that `Worlds.create()` actually returns.
 */
export type ArchWorld = OBC.SimpleWorld<
  OBC.ShadowedScene,
  OBC.OrthoPerspectiveCamera,
  OBF.PostproductionRenderer
>;

export interface Viewer {
  readonly components: OBC.Components;
  readonly world: ArchWorld;
  dispose(): void;
}

export async function createViewer(container: HTMLElement): Promise<Viewer> {
  const components = new OBC.Components();
  const worlds = components.get(OBC.Worlds);
  const world = worlds.create<
    OBC.ShadowedScene,
    OBC.OrthoPerspectiveCamera,
    OBF.PostproductionRenderer
  >();

  const scene = new OBC.ShadowedScene(components);
  const renderer = new OBF.PostproductionRenderer(components, container);
  const camera = new OBC.OrthoPerspectiveCamera(components);

  world.scene = scene;
  world.renderer = renderer;
  world.camera = camera;

  components.init();

  configureScene(scene, renderer);
  configurePostproduction(renderer);

  const [x, y, z] = CAMERA.INITIAL_POSITION;
  const [tx, ty, tz] = CAMERA.INITIAL_TARGET;
  await world.camera.controls.setLookAt(x, y, z, tx, ty, tz);

  // The raycaster must be registered for this world before Highlighter can
  // hit-test against it. Omitting this makes selection silently do nothing.
  components.get(OBC.Raycasters).get(world);

  return {
    components,
    world,
    dispose: () => {
      components.dispose();
    },
  };
}

function configureScene(scene: OBC.ShadowedScene, renderer: OBF.PostproductionRenderer): void {
  scene.setup({
    backgroundColor: new THREE.Color(SCENE.BACKGROUND),
    ambientLight: {
      color: new THREE.Color("#ffffff"),
      intensity: SCENE.AMBIENT_INTENSITY,
    },
    directionalLight: {
      color: new THREE.Color("#ffffff"),
      intensity: SCENE.DIRECTIONAL_INTENSITY,
      position: new THREE.Vector3(15, 25, 12),
    },
    shadows: {
      cascade: SCENE.SHADOW_CASCADES,
      resolution: SCENE.SHADOW_RESOLUTION,
    },
  });

  // Transparent clear so the CSS gradient behind the canvas shows through; the
  // glass UI reads far better over a gradient than over a flat scene colour.
  scene.three.background = null;

  renderer.three.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.three.toneMappingExposure = 1.0;
}

function configurePostproduction(renderer: OBF.PostproductionRenderer): void {
  renderer.showLogo = SHOW_ENGINE_LOGO;

  const { postproduction } = renderer;
  postproduction.enabled = true;
  postproduction.style = OBF.PostproductionAspect.COLOR_SHADOWS;
}
